import { AMBIENCE_BEDS, type AmbienceBedFile } from "../assets/sounds.generated.ts";
import { sniff, type Fetch, type SampleFormat } from "./audio-fetch.ts";
import { BED_IDS, type BedId } from "./ambience-model.ts";

/**
 * Leitos de ambiente (F46): loops estéreo de 30 s que `tools/assets` grava em `public/sons`,
 * baixados e decodificados sob demanda (o modelo pede os que tocam). Leito que falta não faz
 * falta: a síntese que ele cobriria continua tocando.
 *
 * - Os bytes comprimidos ficam na memória depois de baixar (os cinco em WebM: ~1,1 MiB); o
 *   decodificador do Chromium desanexa o `ArrayBuffer`, então decodifica sempre uma cópia.
 * - Decodificados: um LRU de 3 (~7,8 MB de PCM cada, estéreo a 32 kHz). Os que tocam ficam presos
 *   (`pin`) e nunca saem; nunca há mais de 3. Sem vaga (os 3 presos), o próximo espera um soltar.
 * - Uma decodificação por vez, a 32 kHz (`OfflineAudioContext(2, 1, 32000)`), e nunca durante o
 *   render da síntese (o WebKit cai decodificando durante um render offline, F36): espera o
 *   portão `gate` e conta `decodesDuringRender` (tem de ficar 0).
 * - Download com prazo de 20 s (`abort`). Falhou (rede, prazo, arquivo que não é do formato), tenta
 *   o outro formato do mesmo leito antes de contar a tentativa (só esse leito muda de formato);
 *   tenta de novo depois de 30 s e de 120 s e então desiste do leito. Um sucesso zera as
 *   tentativas, e o fim da partida (`trim`) dá outra chance a quem desistiu. Nada aqui rejeita.
 * - Uma decodificação lenta prende a fila até voltar (uma por vez: nunca mais de 3 decodificados),
 *   até 60 s; o resultado vale se o leito ainda está preso. Passados os 60 s, a fila segue e o leito
 *   tenta de novo; a única exceção ao LRU é uma decodificação que volte depois disso, e o resultado
 *   dela é descartado.
 * - No fim da partida, `trim()` solta o PCM dos leitos soltos (os bytes comprimidos ficam) e zera
 *   as desistências.
 * - Formato: começa no que o banco de efeitos escolheu; um WebM que não decodifica troca o banco
 *   para o MP3 (os WebM guardados saem e os leitos presos baixam de novo).
 */

/** Taxa da decodificação: os grilos da floresta de noite ficam só 13 dB abaixo acima de 12 kHz. */
export const AMBIENCE_RATE = 32_000;
/** Leitos decodificados ao mesmo tempo (o LRU). */
export const AMBIENCE_CAPACITY = 3;
/** Prazo de cada download, quanto a fila espera uma decodificação, e as esperas antes de tentar de novo. */
export const BED_TIMEOUT_MS = 20_000;
export const BED_DECODE_GIVE_UP_MS = 60_000;
export const BED_RETRY_MS: readonly number[] = [30_000, 120_000];

export interface BedDecoder {
  decode(bytes: ArrayBuffer): Promise<AudioBuffer>;
}

/** Um leito pronto para tocar: o PCM e o trecho que repete (s). */
export interface ReadyBed {
  buffer: AudioBuffer;
  loopStart: number;
  loopEnd: number;
  gain: number;
}

export type BedState = "frio" | "baixando" | "comprimido" | "decodificando" | "decodificado" | "falhou";

export interface AmbienceBankStats {
  format: SampleFormat | "nenhum";
  states: Record<BedId, BedState>;
  /** Decodificados, do usado há mais tempo ao mais recente, e os presos (tocando). */
  decoded: BedId[];
  pinned: BedId[];
  /** O máximo de decodificados ao mesmo tempo desde o começo (o LRU garante até 3). */
  maxDecoded: number;
  /** Bytes comprimidos guardados e o PCM decodificado agora (float32). */
  cachedBytes: number;
  pcmBytes: number;
  /** Duração do último download e da última decodificação de cada leito (ms). */
  downloadMs: Partial<Record<BedId, number>>;
  decodeMs: Partial<Record<BedId, number>>;
  downloads: number;
  decodes: number;
  /** Decodificações que começaram com o render da síntese em andamento (tem de ser 0). */
  decodesDuringRender: number;
  failed: BedId[];
  error?: string;
}

interface Entry {
  id: BedId;
  file: AmbienceBedFile;
  bytes?: ArrayBuffer | undefined;
  bytesFormat?: SampleFormat | undefined;
  /** Formato só deste leito (o do banco falhou ao baixar) e se a tentativa atual já tentou o outro. */
  override?: SampleFormat | undefined;
  triedOther: boolean;
  buffer?: AudioBuffer | undefined;
  /** Quantos prenderam (o jogo e a bancada podem prender o mesmo). */
  pins: number;
  /** Ordem dos pedidos (decodifica o mais antigo primeiro) e do último uso (o LRU). */
  pinnedAt: number;
  used: number;
  downloading: boolean;
  retrying: boolean;
  attempts: number;
  failed: boolean;
  downloadMs?: number;
  decodeMs?: number;
  waiters: (() => void)[];
}

/** Decodificador do navegador: um contexto offline estéreo a 32 kHz (sem gesto). */
function browserDecoder(): BedDecoder | undefined {
  if (typeof OfflineAudioContext === "undefined") return undefined;
  let ctx: OfflineAudioContext | undefined;
  return { decode: (bytes) => (ctx ??= new OfflineAudioContext(2, 1, AMBIENCE_RATE)).decodeAudioData(bytes) };
}

export class AmbienceBank {
  private readonly fetch: Fetch;
  private readonly decoder: BedDecoder | undefined;
  private readonly gate: () => Promise<unknown>;
  private readonly rendering: () => boolean;
  private readonly hint: () => SampleFormat | "nenhum";
  private readonly now: () => number;
  private readonly timeoutMs: number;
  private readonly decodeGiveUpMs: number;
  private readonly retryMs: readonly number[];
  private readonly capacity: number;
  private readonly entries = new Map<BedId, Entry>();
  /** Formato decidido por uma decodificação (antes dela, o do banco de efeitos). */
  private decided: SampleFormat | undefined;
  private busy: BedId | undefined;
  private tick = 0;
  private maxDecoded = 0;
  private downloads = 0;
  private decodes = 0;
  private duringRender = 0;
  private error: string | undefined;

  constructor(
    o: {
      fetch?: Fetch;
      decoder?: BedDecoder | undefined;
      /** Fim do render da síntese (o portão da decodificação) e se ele está em andamento. */
      gate?: () => Promise<unknown>;
      rendering?: () => boolean;
      /** O formato que o banco de efeitos escolheu. */
      format?: () => SampleFormat | "nenhum";
      beds?: Readonly<Record<BedId, AmbienceBedFile>>;
      now?: () => number;
      timeoutMs?: number;
      decodeGiveUpMs?: number;
      retryMs?: readonly number[];
      capacity?: number;
    } = {},
  ) {
    this.fetch = o.fetch ?? ((url, init) => globalThis.fetch(url, init));
    this.decoder = "decoder" in o ? o.decoder : browserDecoder();
    this.gate = o.gate ?? (() => Promise.resolve());
    this.rendering = o.rendering ?? (() => false);
    this.hint = o.format ?? (() => "webm");
    this.now = o.now ?? (() => performance.now());
    this.timeoutMs = o.timeoutMs ?? BED_TIMEOUT_MS;
    this.decodeGiveUpMs = o.decodeGiveUpMs ?? BED_DECODE_GIVE_UP_MS;
    this.retryMs = o.retryMs ?? BED_RETRY_MS;
    this.capacity = o.capacity ?? AMBIENCE_CAPACITY;
    const beds = o.beds ?? AMBIENCE_BEDS;
    for (const id of BED_IDS) this.entries.set(id, { id, file: beds[id], pins: 0, pinnedAt: 0, used: 0, downloading: false, retrying: false, triedOther: false, attempts: 0, failed: false, waiters: [] });
  }

  private entry(id: BedId): Entry {
    return this.entries.get(id)!;
  }

  /** Formato dos próximos downloads. */
  get format(): SampleFormat | "nenhum" {
    if (!this.decoder) return "nenhum";
    return this.decided ?? this.hint();
  }

  /** Vai tocar: baixa e decodifica, e fica decodificado enquanto preso (cada `pin` pede um `unpin`). */
  pin(id: BedId): void {
    const e = this.entry(id);
    if (e.pins === 0) e.pinnedAt = ++this.tick;
    e.pins++;
    e.used = ++this.tick;
    this.download(e);
    this.pump();
  }

  /** Parou de tocar: continua decodificado até o LRU precisar da vaga. */
  unpin(id: BedId): void {
    const e = this.entry(id);
    if (e.pins === 0) return;
    e.pins--;
    e.used = ++this.tick;
    if (e.pins === 0 && !e.buffer) this.settle(e);
    this.pump();
  }

  /** Só baixa (os bytes comprimidos ficam guardados). */
  prefetch(id: BedId): void {
    this.download(this.entry(id));
  }

  /**
   * Fim da partida: solta o PCM dos leitos que ninguém prende (os bytes comprimidos ficam guardados)
   * e dá outra chance a quem desistiu (uma queda de rede não tira o leito das próximas partidas).
   */
  trim(): void {
    for (const e of this.entries.values()) {
      if (e.pins === 0) e.buffer = undefined;
      e.failed = false;
      e.attempts = 0;
    }
  }

  /** O leito decodificado (marca o uso no LRU). */
  get(id: BedId): ReadyBed | undefined {
    const e = this.entry(id);
    if (!e.buffer) return undefined;
    e.used = ++this.tick;
    return { buffer: e.buffer, loopStart: e.file.loopStart, loopEnd: e.file.loopEnd, gain: e.file.gain };
  }

  /** Espera o leito preso ficar pronto (ou ser solto, ou a desistência). Nunca rejeita. */
  whenReady(id: BedId): Promise<ReadyBed | undefined> {
    const e = this.entry(id);
    if (e.buffer || e.failed || e.pins === 0 || !this.decoder) return Promise.resolve(this.get(id));
    return new Promise((resolve) => e.waiters.push(() => resolve(this.get(id))));
  }

  state(id: BedId): BedState {
    const e = this.entry(id);
    if (e.buffer) return "decodificado";
    if (this.busy === id) return "decodificando";
    if (e.failed) return "falhou";
    if (e.downloading) return "baixando";
    if (e.bytes) return "comprimido";
    return "frio";
  }

  stats(): AmbienceBankStats {
    const all = [...this.entries.values()];
    const decoded = all.filter((e) => e.buffer).sort((a, b) => a.used - b.used);
    return {
      format: this.format,
      states: Object.fromEntries(BED_IDS.map((id) => [id, this.state(id)])) as Record<BedId, BedState>,
      decoded: decoded.map((e) => e.id),
      pinned: all.filter((e) => e.pins > 0).map((e) => e.id),
      maxDecoded: this.maxDecoded,
      cachedBytes: all.reduce((n, e) => n + (e.bytes?.byteLength ?? 0), 0),
      pcmBytes: decoded.reduce((n, e) => n + e.buffer!.length * e.buffer!.numberOfChannels * 4, 0),
      downloadMs: Object.fromEntries(all.filter((e) => e.downloadMs !== undefined).map((e) => [e.id, e.downloadMs])),
      decodeMs: Object.fromEntries(all.filter((e) => e.decodeMs !== undefined).map((e) => [e.id, e.decodeMs])),
      downloads: this.downloads,
      decodes: this.decodes,
      decodesDuringRender: this.duringRender,
      failed: all.filter((e) => e.failed).map((e) => e.id),
      ...(this.error !== undefined && { error: this.error }),
    };
  }

  /** Acorda quem espera o leito. */
  private settle(e: Entry): void {
    for (const w of e.waiters.splice(0)) w();
  }

  /** Baixa e confere os bytes, com prazo; falha tenta o outro formato do leito e depois vira nova tentativa (nunca rejeita). */
  private download(e: Entry): void {
    if (this.format === "nenhum" || e.bytes || e.downloading || e.retrying || e.failed) return;
    const format = e.override ?? this.format;
    e.downloading = true;
    this.downloads++;
    const url = e.file[format];
    const t0 = this.now();
    const abort = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        abort.abort();
        reject(new Error(`${url}: tempo esgotado (${this.timeoutMs} ms)`));
      }, this.timeoutMs);
    });
    void (async () => {
      let fetchFailed = false;
      try {
        const bytes = await Promise.race([
          (async () => {
            const response = await this.fetch(url, { signal: abort.signal });
            if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);
            return response.arrayBuffer();
          })(),
          deadline,
        ]);
        if (sniff(bytes) !== format) throw new Error(`${url}: não é ${format}`);
        // O banco trocou de formato enquanto baixava: estes bytes não servem mais.
        if (format === (e.override ?? this.format)) {
          e.bytes = bytes;
          e.bytesFormat = format;
          e.downloadMs = Math.round(this.now() - t0);
          e.attempts = 0;
          e.triedOther = false;
        }
      } catch (error) {
        this.error ??= error instanceof Error ? error.message : String(error);
        fetchFailed = true;
      } finally {
        clearTimeout(timer);
        e.downloading = false;
      }
      if (fetchFailed && !e.triedOther) {
        // O outro formato deste leito, antes de contar a tentativa (o banco continua no dele).
        e.triedOther = true;
        e.override = format === "webm" ? "mp3" : "webm";
        this.download(e);
      } else if (fetchFailed) {
        e.triedOther = false;
        e.override = undefined;
        this.retry(e);
      }
      this.pump();
    })();
  }

  /** Mais uma falha: tenta de novo depois de 30 s e de 120 s; depois, desiste do leito. */
  private retry(e: Entry): void {
    const wait = this.retryMs[e.attempts++];
    if (wait === undefined) {
      e.failed = true;
      console.warn(`ambiente: leito ${e.id} indisponível; a síntese continua no lugar dele`, this.error ?? "");
      this.settle(e);
      return;
    }
    e.retrying = true;
    setTimeout(() => {
      e.retrying = false;
      this.download(e);
      this.pump();
    }, wait);
  }

  /** A próxima decodificação (uma por vez): o leito preso mais antigo que já tem os bytes. */
  private pump(): void {
    if (this.busy || !this.decoder) return;
    const pinned = [...this.entries.values()].filter((e) => e.pins > 0 && !e.buffer && !e.failed).sort((a, b) => a.pinnedAt - b.pinnedAt);
    // Preso sem bytes (a troca de formato apagou os do outro): baixa de novo.
    for (const e of pinned) if (!e.bytes) this.download(e);
    const next = pinned.find((e) => e.bytes);
    // Sem vaga (os decodificados todos presos): espera um `unpin`, que chama de novo.
    if (!next || !this.hasRoom()) return;
    this.busy = next.id;
    void this.decode(next).finally(() => {
      this.busy = undefined;
      this.pump();
    });
  }

  /** Há vaga, ou um decodificado solto que pode sair. */
  private hasRoom(): boolean {
    const decoded = [...this.entries.values()].filter((e) => e.buffer);
    return decoded.length < this.capacity || decoded.some((e) => e.pins === 0);
  }

  /** Abre vaga no LRU tirando o decodificado solto usado há mais tempo; sem vaga, falso. */
  private makeRoom(): boolean {
    const decoded = [...this.entries.values()].filter((e) => e.buffer);
    let count = decoded.length;
    for (const victim of decoded.filter((e) => e.pins === 0).sort((a, b) => a.used - b.used)) {
      if (count < this.capacity) break;
      victim.buffer = undefined;
      count--;
    }
    return count < this.capacity;
  }

  private async decode(e: Entry): Promise<void> {
    try {
      await this.gate();
    } catch {
      // o portão nunca falha; se falhar, segue
    }
    if (e.pins === 0 || e.buffer || !e.bytes || !this.makeRoom()) return;
    const bytes = e.bytes;
    const format = e.bytesFormat!;
    if (this.rendering()) this.duringRender++;
    this.decodes++;
    const t0 = this.now();
    let buffer: AudioBuffer;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let timedOut = false;
    const giveUp = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        timedOut = true;
        reject(new Error(`decodificação passou de ${this.decodeGiveUpMs} ms`));
      }, this.decodeGiveUpMs);
    });
    try {
      // Uma cópia: o Chromium desanexa o buffer que decodifica, e os bytes ficam para depois do LRU.
      // A fila (`busy`) espera esta voltar, até desistir: uma que nunca volta prenderia todos os leitos.
      buffer = await Promise.race([this.decoder!.decode(bytes.slice(0)), giveUp]);
    } catch (error) {
      this.error ??= `${e.file[format]}: ${error instanceof Error ? error.message : String(error)}`;
      if (!timedOut && !e.override && format === "webm" && this.decided !== "webm") {
        // O banco todo vai para o MP3: os WebM guardados não servem.
        this.decided = "mp3";
        for (const other of this.entries.values()) {
          if (other.bytesFormat !== "webm") continue;
          other.bytes = undefined;
          other.bytesFormat = undefined;
        }
      } else {
        e.bytes = undefined;
        e.bytesFormat = undefined;
        e.override = undefined;
        e.triedOther = false;
        this.retry(e);
      }
      return;
    } finally {
      clearTimeout(timer);
      e.decodeMs = Math.round(this.now() - t0);
    }
    // O formato de um leito só (o do banco falhou ao baixar) não decide o do banco.
    if (!e.override) this.decided ??= format;
    e.attempts = 0;
    // Solto enquanto decodificava (o fim da partida, por exemplo): o PCM não fica na memória.
    if (e.pins === 0) {
      this.settle(e);
      return;
    }
    e.buffer = buffer;
    e.used = ++this.tick;
    this.maxDecoded = Math.max(this.maxDecoded, [...this.entries.values()].filter((x) => x.buffer).length);
    this.settle(e);
  }
}
